import "./SimVerification.scss";

import clsx from "clsx";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import IccidPukHint from "@/assets/onboarding/sim-verification/IccidAndPuk.svg?react";
import { CameraScanner } from "@/components/CameraScanner/CameraScanner";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { HintOverlay } from "@/components/Onboarding/Steps/SimVerification/HintOverlay/HintOverlay";
import { useCheckSIM } from "@/services/api";
import { ICCID_REGEX } from "@/utils/constants";

export type SimVerificationFormProps = {
  iccid: string;
  puk: string;
};

export const formatIccid = (input: string) => {
  const cardValue = input.match(ICCID_REGEX);
  const formatted = cardValue
    ? cardValue
        .slice(1)
        .filter((x) => x)
        .join(" ")
    : input;

  return formatted.substring(0, 24);
};

export const SimVerification: FC = () => {
  const { t } = useTranslation();
  const [showOverlay, setShowOverlay] = useState(false);

  const {
    register,
    watch,
    resetField,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const watchIccid = watch("iccid");
  const shortenedIccid = (watchIccid && watchIccid.replaceAll(" ", "")) || "";
  const iccidIsValid = (v: string) =>
    v ? v.replaceAll(" ", "").length === 20 : false;

  const { data: sim, isFetching: isLoadingCheckSim } = useCheckSIM(
    shortenedIccid,
    {
      query: {
        enabled: iccidIsValid(watchIccid),
        // Clear the iccid field errors when validating again loading
        onSuccess: (sim) => {
          // If the puk is not mandatory for this sim, reset any remaining errors
          if (!sim.puk_mandatory) {
            resetField("puk");
          }
          if (sim?.message === "Sim is not available") {
            setError("iccid", {
              type: "iccidValidError",
            });
          } else {
            clearErrors(["iccid", "puk"]);
          }
        },
      },
    },
  );

  const pukMandatory = sim?.puk_mandatory === true;

  // Register the fields with the parent form with basic required validation
  useEffect(() => {
    register("iccid");
    register("puk");
  }, [register, pukMandatory]);

  return (
    <div id="partner-sim-verification">
      <div className="content-wrapper pb-10">
        <div className="flex flex-col gap-20 pt-10 xl:flex-row">
          <div className="sm:w-1/2">
            <div className="flex flex-col gap-6">
              {isLoadingCheckSim && <LoadingSpinner />}
              <FormField
                id="iccd-input"
                label={t("Onboarding.sections.sim-verification.iccid.label")}
                name="iccid"
                errors={errors}
                register={register}
                type="text"
                placeholder={t(
                  "Onboarding.sections.sim-verification.iccid.placeholder",
                )}
                options={{
                  required: t("label.validation.required"),
                  validate: () => {
                    if (sim?.message === "Sim is not available") {
                      return String(
                        t(
                          "Onboarding.sections.sim-verification.iccid.validation.invalid",
                        ),
                      );
                    }

                    return true;
                  },
                  pattern: {
                    // Valid is either 5 groups of digits.
                    value: /^\d{6}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{2}$/,
                    message: t(
                      "Onboarding.sections.sim-verification.iccid.validation.badFormat",
                    ),
                  },
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    const input = e.target as HTMLInputElement;
                    // JavaScript regex engine constraints, splits ICCID into match groups
                    setValue("iccid", formatIccid(input.value));
                  },
                  iccidValidError: t(
                    "Onboarding.sections.sim-verification.iccid.validation.invalid",
                  ),
                }}
              >
                <button
                  onClick={() => setShowOverlay(!showOverlay)}
                  className="overlay-button"
                  type="button"
                  autoFocus={false}
                  tabIndex={-1}
                >
                  {t("Onboarding.sections.sim-verification.iccid.helpHint")}
                </button>
              </FormField>
              <CameraScanner
                onScan={(value) => {
                  setValue("iccid", formatIccid(value));
                }}
              />
              <FormField
                id="puk-input"
                label={t("Onboarding.sections.sim-verification.puk.label")}
                name="puk"
                errors={errors}
                register={register}
                type="text"
                placeholder={t(
                  "Onboarding.sections.sim-verification.puk.placeholder",
                )}
                options={{
                  required: pukMandatory
                    ? t("translation:label.validation.required")
                    : false,
                  pattern: {
                    value: /^\d{8}$/,
                    message: t(
                      "Onboarding.sections.sim-verification.puk.validation.badFormat",
                    ),
                  },
                  disabled: !pukMandatory,
                  onChange: (e: KeyboardEvent) => {
                    const input = e.target as HTMLInputElement;
                    let value = input.value;
                    if (value.length > 8) {
                      value = value.substring(0, 8);
                    }
                    setValue("puk", value);
                  },
                }}
              />
            </div>
          </div>
          <div className={clsx("hint-image", pukMandatory && "withPuk")}>
            <IccidPukHint />
          </div>
        </div>
      </div>
      <HintOverlay
        setVisible={setShowOverlay}
        visible={showOverlay}
        hint={<IccidPukHint />}
      />
    </div>
  );
};
